<template>
  <div class="flex flex-col gap-4 rounded-md bg-white">
    <div class="grid grid-cols-3 gap-4">
      <Select
        v-model="selectedCounterpartyType"
        :options="counterpartyTypeOptions"
        :label="t('counterpartyType.label')"
      />
      <Select
        :model-value="selectedPaymentMethod"
        :options="paymentMethodOptions"
        :label="t('paymentMethod')"
        @update:model-value="handlePaymentMethodChange"
      />
      <Select v-model="selectionMode" :label="t('selection')" :options="selectionModeOptions" />
      <AppCheckbox
        v-if="$can('crossAllocate', 'LedgerEntry') && selectionMode === 'manual'"
        v-model="showAllCounterpartyLedgerEntries"
        :label="t('showAllCounterpartyLedgerEntries')"
        class="col-start-3 self-end"
      />
    </div>
    <LedgerEntrySelector
      v-if="selectionMode === 'manual'"
      v-model="selectedLedgerEntries"
      :scope="{
        bookingId: booking.id,
        counterpartyType: selectedCounterpartyType,
        includeAllCounterparties: showAllCounterpartyLedgerEntries,
      }"
      amount-to-select-key="amountToRequestPay"
      @update-total-amount="selectedLedgerEntriesAmountSum = $event"
    />
    <div
      v-if="booking.hasPeriodicInvoicing"
      class="grid grid-cols-[max-content_auto_max-content] items-center gap-3 rounded border p-3"
    >
      <Checkbox v-model="isUnallocatedSelected" />
      <div class="font-medium">{{ t('unallocated') }}</div>
      <Input
        :model-value="unallocatedAmount"
        type="number"
        :min="0"
        :min-fraction-digits="2"
        :max-fraction-digits="2"
        :step="0.01"
        adornment="€"
        class="w-40"
        :disabled="!isUnallocatedSelected"
        @update:model-value="(value) => (unallocatedAmount = value ?? null)"
      />
    </div>
    <div class="flex items-center justify-end gap-4">
      <div class="flex font-semibold">
        <div />
        {{
          `${formatCurrency(selectedAmountSum / 100)}${maxAmount !== undefined ? ' / ' + formatCurrency(maxAmount as number) : ''}`
        }}
      </div>
      <CVButton
        variant="default"
        :disabled="!(selectedAmountSum > 0) || (maxAmount && selectedAmountSum / 100 > maxAmount)"
        @click.prevent="chargeAmount"
        >{{
          selectedPaymentMethod === ChargeAmountPaymentMethod.PAYMENT_LINK
            ? t('sendPaymentLink')
            : t('chargeAmount')
        }}</CVButton
      >
      <CVButton variant="secondary" @click.prevent="$emit('onCancel')">{{ t('cancel') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SelectOption } from '@/components/Select.vue';
import Select from '@/components/Select.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import { ChargeAmountPaymentMethod } from '@/entities/payment-terminals/charge-amount-payment-method.enum';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { formatStoredPaymentMethodLabel } from '@/utils/format-stored-payment-method-label';
import { useLedgerEntries } from '@/queries/use-ledger';
import { useStoredPaymentMethods } from '@/queries/use-payment-api';
import { Alert } from '@/services/alert.service';
import { selectPaymentLinkReceiver } from '@/utils/select-payment-link-receiver';
import {
  AutoChargeType,
  type BaseStoredPaymentMethod,
  type ChargeAmountDto,
  FindAllLedgerEntriesSort,
  Order,
  PaymentLinkReceiver,
} from '@carvia/ros-client-types';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LedgerEntrySelector, {
  type SelectedLedgerEntry,
} from '../ledger-entries/LedgerEntrySelector.vue';
import type { Payment } from '@/entities/payments/payment.entity';
import { formatCurrency } from '@/utils/format-numbers';
import { sumBy } from 'lodash';
import { findStoredPaymentMethodCounterparty } from '@/utils/find-stored-payment-method-counterparty';
import Checkbox from '../Checkbox.vue';
import AppCheckbox from '@/components/Checkbox.vue';
import Input from '../Input.vue';

type AllocationMode = 'all' | 'manual';

const props = defineProps<{
  booking: Booking;
  payments: Payment[];
}>();

const emit = defineEmits<{
  (e: 'onCancel'): void;
  (e: 'onSubmit', value: ChargeAmountDto): void;
}>();

const { t, locale } = useI18n();

const OTHER_PAYMENT_METHODS = 'OTHER_PAYMENT_METHODS';

const selectedCounterpartyType = ref<CounterpartyType>(CounterpartyType.CUSTOMER);
const showAllCounterpartyLedgerEntries = ref(false);
const includeOtherStoredPaymentMethods = ref(false);
const selectedPaymentMethod = ref<ChargeAmountPaymentMethod | string>(
  ChargeAmountPaymentMethod.PAYMENT_LINK,
);

const selectionMode = ref<AllocationMode>('all');
const selectedLedgerEntriesAmountSum = ref(0);
const isUnallocatedSelected = ref(false);
const unallocatedAmount = ref<number | null>(null);

const selectedAmountSum = computed(() => {
  const unallocatedAmountInCents =
    props.booking.hasPeriodicInvoicing &&
    isUnallocatedSelected.value &&
    Number(unallocatedAmount.value) > 0
      ? Math.round(Number(unallocatedAmount.value) * 100)
      : 0;
  return selectedLedgerEntriesAmountSum.value + unallocatedAmountInCents;
});

const maxAmount = computed(() =>
  selectedPaymentMethod.value === ChargeAmountPaymentMethod.DEPOSIT
    ? props.booking.blockedCreditCardAmount
    : undefined,
);

const selectionModeOptions = computed<SelectOption[]>(() => [
  { label: t('selectAll'), value: 'all' },
  { label: t('selectManual'), value: 'manual' },
]);

const counterpartyTypeOptions = useCounterpartyTypeOptions();

const { data: storedPaymentMethods } = useStoredPaymentMethods(props.booking.id);

const belongsToSelectedPayer = (method: BaseStoredPaymentMethod) =>
  findStoredPaymentMethodCounterparty(props.booking, method) === selectedCounterpartyType.value;

const matchingStoredPaymentMethods = computed(
  () => storedPaymentMethods.value?.filter((method) => belongsToSelectedPayer(method)) ?? [],
);

const otherStoredPaymentMethods = computed(
  () => storedPaymentMethods.value?.filter((method) => !belongsToSelectedPayer(method)) ?? [],
);

const paymentMethodOptions = computed<SelectOption<'number' | 'text'>[]>(() => [
  ...[
    {
      label: t('paymentLink'),
      value: ChargeAmountPaymentMethod.PAYMENT_LINK,
    },
  ],
  ...(props.booking.blockedCreditCardAmount &&
  selectedCounterpartyType.value == CounterpartyType.CUSTOMER
    ? [
        {
          label: t('deposit'),
          value: ChargeAmountPaymentMethod.DEPOSIT,
        },
      ]
    : []),
  ...matchingStoredPaymentMethods.value.map((method) => ({
    label: formatStoredPaymentMethodLabel(method),
    value: method.id,
  })),
  ...(includeOtherStoredPaymentMethods.value
    ? otherStoredPaymentMethods.value.map((method) => ({
        label: formatStoredPaymentMethodLabel(method),
        value: method.id,
      }))
    : otherStoredPaymentMethods.value.length > 0
      ? [
          {
            label: t('otherPaymentMethods'),
            value: OTHER_PAYMENT_METHODS,
          },
        ]
      : []),
]);

const handlePaymentMethodChange = async (value: string | number | null | undefined) => {
  if (value == null) return;
  const nextValue = String(value);
  if (nextValue !== OTHER_PAYMENT_METHODS) {
    selectedPaymentMethod.value = nextValue;
    return;
  }

  const previousValue = selectedPaymentMethod.value;
  // Sync the native <select> while the confirm dialog is open.
  selectedPaymentMethod.value = OTHER_PAYMENT_METHODS;

  const { isConfirmed } = await Alert.fire({
    titleText: t('otherPaymentMethodsConfirmTitle'),
    text: t('otherPaymentMethodsConfirmText'),
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: t('otherPaymentMethodsDecline'),
    confirmButtonText: t('otherPaymentMethodsConfirm'),
  });
  if (!isConfirmed) {
    selectedPaymentMethod.value = previousValue;
    return;
  }

  includeOtherStoredPaymentMethods.value = true;
  selectedPaymentMethod.value = ChargeAmountPaymentMethod.PAYMENT_LINK;
};

const {
  data: ledgerEntriesData,
  status,
  error,
} = useLedgerEntries(
  computed(() => ({
    bookingIds: [props.booking.id],
    counterpartyTypes:
      showAllCounterpartyLedgerEntries.value && selectionMode.value === 'manual'
        ? undefined
        : [selectedCounterpartyType.value],
    sort: [FindAllLedgerEntriesSort.DUE_DATE],
    order: Order.ASC,
    limit: 999,
  })),
);

const ledgerEntries = useFlattenPaginatedData(ledgerEntriesData);

const selectedLedgerEntries = ref<SelectedLedgerEntry[]>(
  ledgerEntries.value
    .filter(({ amountToRequestPay }) => amountToRequestPay !== 0)
    .map(({ id }) => ({
      id,
    })),
);

watch(
  [ledgerEntries, selectionMode, selectedCounterpartyType, showAllCounterpartyLedgerEntries],
  () => {
    selectedLedgerEntries.value = ledgerEntries.value
      .filter(({ amountToRequestPay }) => amountToRequestPay !== 0)
      .map(({ id }) => ({
        id,
      }));
    if (selectionMode.value === 'all') {
      selectedLedgerEntriesAmountSum.value = sumBy(ledgerEntries.value, 'amountToRequestPay');
    }
  },
  { immediate: true },
);

watch([selectedCounterpartyType], () => {
  selectedPaymentMethod.value = ChargeAmountPaymentMethod.PAYMENT_LINK;
  includeOtherStoredPaymentMethods.value = false;
});

watch(selectionMode, (mode) => {
  if (mode === 'all') {
    showAllCounterpartyLedgerEntries.value = false;
  }
});

const chargeAmount = async () => {
  if (selectedPaymentMethod.value !== ChargeAmountPaymentMethod.PAYMENT_LINK) {
    const alertResult = await Alert.fire({
      titleText: t('reallyChargeTitle'),
      text:
        selectedPaymentMethod.value === ChargeAmountPaymentMethod.DEPOSIT
          ? t('reallyCaptureFromDeposit')
          : t('reallyChargeFromCreditCardText'),

      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: t('declineCharge'),
      confirmButtonText: t('confirmCharge'),
    });
    if (!alertResult.isConfirmed) return;
  }
  let type: AutoChargeType | null = null;
  let key: string | undefined = undefined;
  let paymentLinkReceiver: PaymentLinkReceiver | undefined;
  if (selectedCounterpartyType.value === CounterpartyType.CUSTOMER) {
    paymentLinkReceiver = PaymentLinkReceiver.CUSTOMER;
  } else if (selectedCounterpartyType.value === CounterpartyType.AGENCY) {
    paymentLinkReceiver = PaymentLinkReceiver.AGENCY;
  }

  switch (selectedPaymentMethod.value) {
    case ChargeAmountPaymentMethod.PAYMENT_LINK: {
      type = AutoChargeType.PAYMENT_LINK;
      paymentLinkReceiver = await selectPaymentLinkReceiver(props.booking, paymentLinkReceiver);
      if (!paymentLinkReceiver) return;
      break;
    }
    case ChargeAmountPaymentMethod.DEPOSIT: {
      type = AutoChargeType.DEPOSIT;
      break;
    }
    default: {
      const storedPaymentMethod = storedPaymentMethods.value?.find(
        (method) => method.id === selectedPaymentMethod.value,
      );
      if (!storedPaymentMethod) return;
      type = AutoChargeType.PSEUDO_NUMBER;
      key = storedPaymentMethod.id;
    }
  }

  const chargeAmountDto: ChargeAmountDto = {
    bookingId: props.booking.id,
    amount: selectedAmountSum.value / 100,
    types: [type],
    key,
    paymentLinkReceiver,
    counterpartyType: selectedCounterpartyType.value,
    ledgerEntries: selectedLedgerEntries.value,
  };
  emit('onSubmit', chargeAmountDto);
};
</script>

<i18n lang="json">
{
  "en": {
    "paymentMethod": "Payment Method",
    "paymentLink": "Payment Link",
    "amount": "Amount",
    "chargeAmount": "Charge Amount",
    "sendPaymentLink": "Send Payment Link",
    "position": "Position",
    "dueDate": "Due Date",
    "sum": "Total Amount",
    "error": {
      "mustBePositive": "Sum must be greater than 0",
      "cannotExceedOpen": "Can not exceed open amount"
    },
    "reallyChargeTitle": "Really charge from Customer?",
    "reallyChargeFromCreditCardText": "The specified amount is debited directly from the customer's credit card.",
    "reallyCaptureFromDeposit": "The specified amount is debited directly from the blocked deposit.",
    "confirmCharge": "Charge Amount",
    "declineCharge": "Cancel",
    "selection": "Auswahl",
    "selectAll": "All",
    "selectManual": "Select manually",
    "deposit": "Deposit",
    "unallocated": "Unallocated",
    "showAllCounterpartyLedgerEntries": "Show ledger entries of all counterparties",
    "otherPaymentMethods": "Other payment method…",
    "otherPaymentMethodsConfirmTitle": "Show other payment methods?",
    "otherPaymentMethodsConfirmText": "These payment methods may not belong to the selected counterparty. Only continue if you are sure the method belongs to this person.",
    "otherPaymentMethodsConfirm": "Show other methods",
    "otherPaymentMethodsDecline": "Cancel"
  },
  "de": {
    "paymentMethod": "Zahlungsart",
    "paymentLink": "Zahlungslink",
    "amount": "Betrag",
    "chargeAmount": "Betrag abbuchen",
    "sendPaymentLink": "Zahlungslink versenden",
    "position": "Position",
    "dueDate": "Fälligkeitsdatum",
    "sum": "Gesamtbetrag",
    "error": {
      "mustBePositive": "Summe muss über 0 sein",
      "cannotExceedOpen": "Darf nicht über dem offenen Betrag von {openAmount} liegen"
    },
    "reallyChargeTitle": "Wirklich vom Kunden abbuchen?",
    "reallyChargeFromCreditCardText": "Der angegebene Betrag wird direkt von der Kreditkarte des Kunden abgebucht.",
    "reallyCaptureFromDeposit": "Der angegebene Betrag wird direkt von der blockierten Kaution abgebucht.",
    "confirmCharge": "Betrag abbuchen",
    "declineCharge": "Abbrechen",
    "selection": "Auswahl",
    "selectAll": "Alles",
    "selectManual": "Selber auswählen",
    "deposit": "Kaution",
    "unallocated": "Nicht zugeordnet",
    "showAllCounterpartyLedgerEntries": "Forderungseinträge aller Gegenparteien anzeigen",
    "otherPaymentMethods": "Andere Zahlungsmethode…",
    "otherPaymentMethodsConfirmTitle": "Andere Zahlungsmethoden anzeigen?",
    "otherPaymentMethodsConfirmText": "Diese Zahlungsmethoden gehören möglicherweise nicht zur ausgewählten Gegenpartei. Fahren Sie nur fort, wenn Sie sicher sind, dass die Methode zu dieser Person gehört.",
    "otherPaymentMethodsConfirm": "Andere Methoden anzeigen",
    "otherPaymentMethodsDecline": "Abbrechen"
  },
  "es": {
    "paymentMethod": "Método de pago",
    "paymentLink": "Enlace de pago",
    "amount": "Importe",
    "chargeAmount": "Cobrar importe",
    "sendPaymentLink": "Enviar enlace de pago",
    "position": "Posición",
    "dueDate": "Fecha de vencimiento",
    "sum": "Importe total",
    "error": {
      "mustBePositive": "La suma debe ser mayor que 0",
      "cannotExceedOpen": "No puede superar el importe pendiente"
    },
    "reallyChargeTitle": "¿Cobrar al cliente?",
    "reallyChargeFromCreditCardText": "El importe indicado se cargará directamente en la tarjeta del cliente.",
    "reallyCaptureFromDeposit": "El importe indicado se cargará contra el depósito retenido.",
    "confirmCharge": "Cobrar importe",
    "declineCharge": "Cancelar",
    "selection": "Selección",
    "selectAll": "Todo",
    "selectManual": "Seleccionar manualmente",
    "deposit": "Depósito",
    "unallocated": "Sin imputar",
    "showAllCounterpartyLedgerEntries": "Mostrar partidas de todas las contrapartes",
    "otherPaymentMethods": "Otro método de pago…",
    "otherPaymentMethodsConfirmTitle": "¿Mostrar otros métodos de pago?",
    "otherPaymentMethodsConfirmText": "Estos métodos de pago pueden no pertenecer a la contraparte seleccionada. Continúe solo si está seguro de que el método pertenece a esta persona.",
    "otherPaymentMethodsConfirm": "Mostrar otros métodos",
    "otherPaymentMethodsDecline": "Cancelar"
  }
}
</i18n>
